import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { stravaApi, StravaAnalysis } from "@/api/strava";

// Map personality types to emojis
const PERSONALITY_EMOJIS: Record<string, string> = {
  "The Machine": "🤖",
  "Weekend Warrior": "⚔️",
  "Sunrise Chaser": "🌅",
  "Night Owl Runner": "🦉",
  "Distance Hunter": "🎯",
  "Speed Demon": "⚡️",
  "Steady Climber": "🏔️",
  "Comeback Kid": "💪",
  "The Urban Explorer": "🚀",
};

// Mock data for dev preview
export const MOCK_ANALYSIS: StravaAnalysis = {
  id: "mock-id",
  userId: "mock-user",
  personalityType: "The Urban Explorer",
  personalityEmoji: "🚀",
  personalityDesc:
    "You own the concrete jungle. Your consistency in early morning runs shows you beat the traffic and the excuses.",
  totalDistance: 452,
  totalActivities: 34,
  avgWeeklyRuns: 3.2,
  avgDistance: 13.3,
  longestStreak: 12,
  bestMonth: "March 2024",
  recommendedGoal: 5,
  successPrediction: 88,
  recommendedTier: "Intermediate",
  matchReasoning:
    "Your consistent 12-day streak suggests your recovery rate is optimal. You're ready to ramp up volume without injury risk.",
  funFactStat: "5:30 AM",
  funFactMessage:
    "You run before the sun. You're basically a vampire, but for fitness. This is your peak performance window.",
  motivation: "Pain is temporary. Strava Kudos are forever.",
  activitiesAnalyzed: 34,
  periodStart: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
  periodEnd: new Date().toISOString(),
  createdAt: new Date().toISOString(),
};

export function AnalysisSummary() {
  const navigate = useNavigate();
  const location = useLocation();
  const [analysis, setAnalysis] = useState<StravaAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for mock data in location state (dev mode)
  const mockData = (location.state as { mockData?: StravaAnalysis })?.mockData;

  // Fetch analysis on mount (or use mock data)
  useEffect(() => {
    // If mock data provided, use it directly
    if (mockData) {
      setAnalysis(mockData);
      setIsLoading(false);
      return;
    }

    const fetchAnalysis = async () => {
      try {
        const { analysis } = await stravaApi.getAnalysis();
        if (analysis) {
          setAnalysis(analysis);
        } else {
          // No analysis exists, redirect to profile-success to run one
          navigate("/profile-success", { replace: true });
        }
      } catch (error) {
        console.error("Failed to fetch analysis:", error);
        navigate("/profile-success", { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalysis();
  }, [navigate, mockData]);

  const handleViewReport = () => {
    // Pass mock data through if in dev mode
    if (mockData) {
      navigate("/analysis-report", { state: { mockData } });
    } else {
      navigate("/analysis-report");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#102210] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!analysis) {
    return null; // Will redirect
  }

  const emoji = PERSONALITY_EMOJIS[analysis.personalityType] || "🏃";

  return (
    <div className="min-h-screen bg-[#102210] relative overflow-hidden">
      {/* Background Effects */}
      {/* Dot pattern */}
      <div
        className="absolute inset-0 z-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 50%, #0df20d 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }}
      />
      {/* Blurred green orb */}
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/20 rounded-full blur-[128px] pointer-events-none" />

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-md mx-auto flex flex-col min-h-screen px-6 py-8 justify-between">
        {/* Header */}
        <header className="w-full flex flex-col items-center pt-8">
          {/* Home Indicator */}
          <div className="w-12 h-1 bg-white/10 rounded-full mb-6" />
          <p className="text-white/60 tracking-wider text-sm font-medium uppercase mb-2">
            Analysis Ready
          </p>
          <h1 className="text-white text-3xl font-bold leading-tight text-center">
            Your Run Persona
          </h1>
        </header>

        {/* Main Card */}
        <main className="w-full flex-grow flex items-center justify-center py-6">
          <div className="relative group w-full">
            {/* Card Glow Effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-b from-primary/50 to-primary/10 rounded-3xl blur opacity-30 group-hover:opacity-50 transition duration-500" />

            {/* Card Body */}
            <div className="relative flex flex-col items-center bg-[#162e16]/80 backdrop-blur-xl border border-white/5 rounded-3xl p-8 shadow-2xl overflow-hidden">
              {/* Decorative top shine */}
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

              {/* Icon Container */}
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-primary rounded-full blur-2xl opacity-20" />
                <div className="relative flex items-center justify-center w-24 h-24 bg-gradient-to-br from-primary/20 to-transparent rounded-full border border-primary/30 shadow-[0_0_15px_rgba(13,242,13,0.3)]">
                  <span
                    className="text-6xl"
                    style={{
                      filter: "drop-shadow(0 0 8px rgba(13, 242, 13, 0.5))",
                    }}
                  >
                    {emoji}
                  </span>
                </div>
              </div>

              {/* Persona Title */}
              <h2 className="text-white text-2xl font-bold tracking-tight text-center mb-1">
                {analysis.personalityType}
              </h2>
              <p className="text-white/50 text-sm font-light text-center mb-8">
                {analysis.personalityDesc.split(".")[0]}.
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4 w-full mb-8">
                <div className="flex flex-col items-center justify-center p-4 rounded-3xl bg-white/5 border border-white/5">
                  <span className="material-symbols-outlined text-primary mb-2 text-2xl">
                    location_on
                  </span>
                  <span className="text-2xl font-bold text-white tracking-tight">
                    {Math.round(analysis.totalDistance)}
                  </span>
                  <span className="text-xs font-medium text-white/40 uppercase tracking-widest">
                    Kilometers
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center p-4 rounded-3xl bg-white/5 border border-white/5">
                  <span className="material-symbols-outlined text-primary mb-2 text-2xl">
                    directions_run
                  </span>
                  <span className="text-2xl font-bold text-white tracking-tight">
                    {analysis.totalActivities}
                  </span>
                  <span className="text-xs font-medium text-white/40 uppercase tracking-widest">
                    Total Runs
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={handleViewReport}
                className="w-full group/btn relative flex items-center justify-center gap-2 bg-primary hover:bg-[#0be00b] text-[#102210] font-bold text-base py-4 px-6 rounded-full transition-all duration-300 shadow-[0_0_20px_rgba(13,242,13,0.4)] hover:shadow-[0_0_30px_rgba(13,242,13,0.6)] hover:scale-[1.02]"
              >
                <span>View Full Report</span>
                <span className="material-symbols-outlined text-lg transition-transform group-hover/btn:translate-x-1">
                  arrow_forward
                </span>
              </button>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="w-full pb-6 flex flex-col items-center justify-center gap-4 text-center">
          <p className="text-[10px] text-white/40 font-light tracking-widest uppercase">
            Powered by Strava
          </p>
        </footer>
      </div>
    </div>
  );
}
